import { useEffect, useState } from "react";
import { DatabaseBackup, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const save = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
export { save as saveBlob };

export function BackupPanel() {
  const [last, setLast] = useState([]);
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState("");
  const [loaded, setLoaded] = useState(false);
  const load = () => api.get("/backup/status").then(({ data }) => { setLast(data.last); setLoaded(true); });
  useEffect(() => { load(); }, []);
  const run = async (files) => {
    setBusy(files ? "full" : "data");
    try {
      const { data: { id } } = await api.post("/backup/start", null, { params: { files } });
      let job;
      do {
        await new Promise((r) => setTimeout(r, 1500));
        job = (await api.get(`/backup/job/${id}`)).data;
        setProgress(job.total ? `${job.done}/${job.total} file` : "Preparazione...");
      } while (job.status === "running");
      if (job.status !== "ready") throw new Error("Backup non riuscito, riprova");
      setProgress("Download...");
      const res = await api.get(`/backup/download/${id}`, { responseType: "blob" });
      save(res.data, `backup_suk_${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success("Backup scaricato: conservalo in un luogo sicuro");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
    setProgress("");
  };
  const l = last[0];
  return (
    <section className="rounded-2xl bg-white border border-[var(--border)] p-5 sm:p-6 space-y-4" data-testid="backup-panel">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><DatabaseBackup className="h-5 w-5 text-[var(--primary)]" /> Backup</h2>
      <p className="text-sm text-[var(--muted)]">Il database pubblicato ha backup automatici della piattaforma (ogni ora e ogni giorno per 7 giorni, settimanali per 4 settimane, mensili per 12 mesi). Foto, documenti e firme non hanno un backup separato: scarica qui una copia completa almeno una volta alla settimana.</p>
      <p className="text-sm" data-testid="backup-last">{!loaded ? "Caricamento..." : l ? <>Ultimo backup: <b>{new Date(l.at).toLocaleString("it-IT")}</b> da {l.by} · {l.with_files ? `${l.files} file` : "solo dati"}{l.missing ? ` · ${l.missing} file non leggibili` : ""}</> : <b className="text-red-600">Nessun backup scaricato finora</b>}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => run(true)} disabled={!!busy} className="btn-primary h-11 px-5" data-testid="backup-full-button">{busy === "full" ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Scarica backup completo</button>
        <button onClick={() => run(false)} disabled={!!busy} className="btn-secondary h-11 px-5" data-testid="backup-data-button">{busy === "data" ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Solo dati (veloce)</button>
        {progress && <span className="text-sm text-[var(--muted)] self-center" data-testid="backup-progress">{progress}</span>}
      </div>
      <details className="text-sm" data-testid="restore-procedure">
        <summary className="cursor-pointer font-semibold">Procedura di ripristino</summary>
        <ol className="list-decimal pl-5 mt-2 space-y-1 text-[var(--muted)]">
          <li>Database cancellato o danneggiato negli ultimi 7 giorni: scrivi a support@emergent.sh indicando il Job ID dell'app e l'ora a cui tornare.</li>
          <li>Per un periodo più vecchio indica la data: sono disponibili i backup settimanali (4 settimane) e mensili (12 mesi).</li>
          <li>Foto o documenti persi: invia al supporto il file ZIP di backup completo (cartella file/ e dati/files.json).</li>
          <li>Dopo il ripristino reimposta le password del personale da Impostazioni: nel backup non sono incluse.</li>
          <li>Controlla nel Registro attività che tutto sia tornato in ordine.</li>
        </ol>
      </details>
    </section>
  );
}
